import { Icon } from '@/components/icon'
import { DropdownMenuLabel, DropdownMenuItem, Skeleton } from '@/components/ui'
import { AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { useSuspenseRegionais } from '@/features/regionais/hooks'
import type { TLocalidade } from '@/infra/i18n'
import type { TUnidadesDeNegocioDisponiveisData } from '@/types/unidade-negocio'

interface RegionalItemProps {
  regional: TUnidadesDeNegocioDisponiveisData
  disabled: boolean
  onClick: (regional: TUnidadesDeNegocioDisponiveisData) => void
}

interface RegionalListProps {
  user: { unidadeDeNegocioId: string }
  onClick: (regional: TUnidadesDeNegocioDisponiveisData) => void
}

const PAIS_LABEL: Record<TLocalidade, string> = {
  'pt-BR': 'Brasil',
  'es-CL': 'Chile',
}

function RegionalItem({ regional, disabled, onClick }: Readonly<RegionalItemProps>) {
  return (
    <AlertDialogTrigger asChild disabled={disabled} className="cursor-pointer data-[disabled]:opacity-100">
      <DropdownMenuItem
        onClick={() => onClick(regional)}
        data-active={disabled}
        className="group gap-2 p-2 data-[active=true]:text-red-800"
        data-testid={`regional-item-${regional.id}`}
      >
        <div className="flex size-6 items-center justify-center rounded-md border">
          <Icon name="building" className="size-4 shrink-0 data-[active=true]:text-red-800" />
        </div>
        {regional.nome}
        <Icon
          name="dot"
          className="ml-auto size-6 text-xs tracking-widest text-red-800 group-data-[active=false]:last:hidden"
        />
      </DropdownMenuItem>
    </AlertDialogTrigger>
  )
}

export const ListaRegionais = ({ user: { unidadeDeNegocioId }, onClick }: RegionalListProps) => {
  const { data: regionais } = useSuspenseRegionais()

  const regionaisBrasil = regionais?.unidadesDisponiveis.filter((r) => r.localidade === 'pt-BR') ?? []
  const regionaisChile = regionais?.unidadesDisponiveis.filter((r) => r.localidade === 'es-CL') ?? []

  const grupos = [
    { localidade: 'pt-BR' as const, items: regionaisBrasil },
    { localidade: 'es-CL' as const, items: regionaisChile },
  ].filter((grupo) => grupo.items.length > 0)

  return (
    <div className="max-h-[60vh] overflow-auto md:max-h-[80vh]">
      {grupos.map((grupo) => (
        <div key={grupo.localidade}>
          <DropdownMenuLabel className="text-muted-foreground text-xs">
            {PAIS_LABEL[grupo.localidade]}
          </DropdownMenuLabel>
          {grupo.items.map((regional) => (
            <RegionalItem
              key={regional.id}
              regional={regional}
              disabled={regional.id === unidadeDeNegocioId}
              onClick={onClick}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

export const ListaRegionaisSkeleton = () => {
  return (
    <>
      <div className="flex gap-1" data-testid="regional-item-skeleton">
        <Skeleton className="mb-2 h-8 w-8 animate-pulse" />
        <Skeleton className="mb-2 h-8 w-full animate-pulse" />
      </div>
      <div className="flex gap-1">
        <Skeleton className="mb-2 h-8 w-8 animate-pulse" />
        <Skeleton className="mb-2 h-8 w-full animate-pulse" />
      </div>
      <div className="flex gap-1">
        <Skeleton className="mb-2 h-8 w-8 animate-pulse" />
        <Skeleton className="mb-2 h-8 w-full animate-pulse" />
      </div>
      <div className="flex gap-1">
        <Skeleton className="mb-2 h-8 w-8 animate-pulse" />
        <Skeleton className="mb-2 h-8 w-full animate-pulse" />
      </div>
    </>
  )
}
